// Accesso.tsx — «Continua con Google» / «Continua con Apple», poi (solo la prima volta) data di nascita e nome.
// Sotto l'età minima non si entra (controllato dal server). Giocare resta possibile anche senza account.
import { Palco } from "../componenti/Palco.tsx";
import { AvvisoSospensione, datiSospensione } from "../componenti/AvvisoSospensione.tsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CampoNome } from "../gioco/CampoNome.tsx";
import { accedi, annullaRegistrazione, nuovoNonce, registra, useAccount, useAvatar } from "../servizi/account.ts";
import { Avatar } from "../componenti/Avatar.tsx";
import { vai } from "../servizi/percorso.ts";
import { accediConApple, disponibileSuQuestaPiattaforma, pulsanteGoogle } from "../servizi/accessoWeb.ts";
import { ErroreRete } from "../servizi/api.ts";
import { eNumero, eTesto, leggiImpostazione } from "../servizi/impostazioni.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import type { ControlloTema } from "../temi/useTema.ts";
import { BASE_API, IMP_APP, PERCORSO_AVATAR, PERCORSO_PRIVACY, PREFERENZE } from "../costanti.ts";
import { Indietro } from "../componenti/Indietro.tsx";

const ERRORI_NOTI = [
  "token_non_valido",
  "registrazione_scaduta",
  "eta_insufficiente",
  "data_non_valida",
  "sospeso",
  "account_spenti",
  "fornitore_non_configurato",
  "offline",
  "timeout",
  "troppe_richieste",
  "nome_vietato",
];
const codiceErrore = (e: unknown): string => {
  let c = e instanceof ErroreRete ? e.codice : "generico";
  // Google/Apple non rispondono al server: per chi usa l'app è come un «riprova tra poco».
  if (c === "fornitore_non_raggiungibile" || c === "servizio_non_disponibile") c = "timeout";
  return ERRORI_NOTI.includes(c) ? c : "generico";
};

export function Accesso({ tema, onFatto, onIndietro }: { tema: ControlloTema; onFatto: () => void; onIndietro: () => void }) {
  const { t, i18n } = useTranslation();
  const account = useAccount();
  const [errore, setErrore] = useState<string | null>(null);
  // v021: con «sospeso» il server manda il motivo e il codice del caso
  const [sospensione, setSospensione] = useState<ReturnType<typeof datiSospensione>>(null);
  const [invio, setInvio] = useState(false);
  const google = useRef<HTMLDivElement>(null);
  // Il nonce lo emette il server (vale una volta): uno nuovo per ogni tentativo.
  const [nonce, setNonce] = useState<string | null>(null);
  const [giro, setGiro] = useState(0);
  const clientGoogle = leggiImpostazione(IMP_APP.googleClientIdWeb, "", eTesto);
  const serviceApple = leggiImpostazione(IMP_APP.appleServiceId, "", eTesto);
  const web = disponibileSuQuestaPiattaforma();

  // v045: appena registrati si torna subito dove si era (prima c'era «Le tue scelte»: ora si chiedono dopo la prima
  // partita, componenti/DomandaScelte.tsx; con le scelte già fatte da ospite valgono quelle, copiate sull'account)
  const fatto = useRef(false);
  useEffect(() => {
    if (account.tipo !== "registrato" || fatto.current) return;
    fatto.current = true;
    onFatto();
  }, [account.tipo, onFatto]);

  useEffect(() => {
    if (!web || account.tipo === "registrazione") return;
    let vivo = true;
    setNonce(null);
    nuovoNonce()
      .then((n) => vivo && setNonce(n))
      .catch((e: unknown) => vivo && setErrore(codiceErrore(e)));
    return () => {
      vivo = false;
    };
  }, [web, account.tipo, giro]);

  const conToken = (fornitore: "google" | "apple", n: string) => (token: string) => {
    setInvio(true);
    setErrore(null);
    accedi(fornitore, token, n)
      .catch((e: unknown) => {
        setErrore(codiceErrore(e));
        setSospensione(e instanceof ErroreRete ? datiSospensione(e.dettagli) : null);
      })
      .finally(() => {
        setInvio(false);
        setGiro((g) => g + 1); // il nonce è consumato: se ne chiede un altro
      });
  };

  // Il pulsante di Google si (ri)prepara con ogni nonce nuovo.
  useEffect(() => {
    if (!web || !clientGoogle || !google.current || !nonce || account.tipo === "registrazione") return;
    pulsanteGoogle(google.current, clientGoogle, nonce, i18n.language, conToken("google", nonce)).catch(() => setErrore("script"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [web, clientGoogle, i18n.language, account.tipo, nonce]);

  // L'errore resta qui (non nella registrazione): se l'età non basta si torna a questa schermata e lo si vede.
  if (account.tipo === "registrazione")
    return <Registrazione tema={tema} etaMinima={account.etaMinima} email={account.email} errore={errore} onErrore={setErrore} />;

  return (
    <div className="pagina pagina-account ld-pagina">
      <header className="ld-barra">
        <Indietro onClick={onIndietro} />
        <span />
      </header>
      <main className="account-centro">
        <Palco mascotte="caso" colore="lilla" titolo={t("account.accedi_titolo")} sottotitolo={t("account.accedi_testo")} />
        {!web ? (
          <p className="nota" role="status">
            {t("account.solo_web")}
          </p>
        ) : (
          <div className="account-fornitori" aria-busy={invio}>
            {clientGoogle && <div ref={google} className="pulsante-google" />}
            {serviceApple && (
              <button
                type="button"
                className="pulsante pulsante-apple"
                disabled={invio || !nonce}
                onClick={() => {
                  if (!nonce) return;
                  setErrore(null);
                  const n = nonce;
                  accediConApple(serviceApple, n)
                    .then((tok) => tok && conToken("apple", n)(tok))
                    .catch(() => setErrore("script"));
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M16.4 12.6c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.8-1.6 0-3.1 1-4 2.4-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3 2.4 1.2 0 1.7-.8 3.1-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.1-1.2 2.9-2.4.9-1.4 1.3-2.7 1.3-2.8 0 0-2.4-1-2.4-3.9zM14 5.4c.7-.8 1.1-1.9 1-3-1 0-2.1.7-2.8 1.5-.6.7-1.2 1.8-1 2.9 1.1.1 2.1-.6 2.8-1.4z" />
                </svg>
                {t("account.continua_apple")}
              </button>
            )}
            {!clientGoogle && !serviceApple && <p className="nota">{t("account.non_configurato")}</p>}
          </div>
        )}
        {errore === "sospeso" && sospensione ? (
          <AvvisoSospensione dati={sospensione} chi="account" />
        ) : errore && (
          <p className="admin-errore" role="alert">
            {t(`account.errore_${errore}`, { defaultValue: t("account.errore_generico"), eta: leggiImpostazione(IMP_APP.etaMinima, 16, eNumero) })}
          </p>
        )}
        <ul className="account-vantaggi">
          <li>{t("account.vantaggio_profilo")}</li>
          <li>{t("account.vantaggio_quota")}</li>
          <li>{t("account.vantaggio_filtri")}</li>
        </ul>
        <p className="nota-centrata">
          {t("account.senza_account")}{" "}
          <a href={`${BASE_API}${PERCORSO_PRIVACY}?lingua=${encodeURIComponent(i18n.language)}`} target="_blank" rel="noopener">
            {t("legale.privacy")}
          </a>
        </p>
      </main>
    </div>
  );
}

function Registrazione(p: {
  tema: ControlloTema;
  etaMinima: number;
  email: string | null;
  errore: string | null;
  onErrore: (e: string | null) => void;
}) {
  const { etaMinima, email, errore, onErrore: setErrore } = p;
  const { t } = useTranslation();
  const [nome, setNome] = useState(() => leggiPreferenza(PREFERENZE.nome) ?? "");
  // La data già dichiarata alla prima apertura si propone (si può correggere).
  const [data, setData] = useState(() => leggiPreferenza(PREFERENZE.nascita) ?? "");
  const [invio, setInvio] = useState(false);
  const avatar = useAvatar();
  const minima = leggiImpostazione(IMP_APP.etaMinima, etaMinima, eNumero);
  const oggi = new Date().toISOString().slice(0, 10);

  return (
    <div className="pagina pagina-account ld-pagina">
      <main className="account-centro">
        {/* v041: la faccina con la matita: un tocco e si scelgono avatar e colore già adesso */}
        <Palco
          colore="lilla"
          titolo={t("account.registrati_titolo")}
          compatto
          figura={
            <button
              type="button"
              className="ld-profilo-avatar"
              aria-label={t("avatar.cambia")}
              onClick={() => {
                // nome e data restano scritti al ritorno
                if (nome.trim()) salvaPreferenza(PREFERENZE.nome, nome.trim());
                if (data) salvaPreferenza(PREFERENZE.nascita, data);
                vai(PERCORSO_AVATAR);
              }}
            >
              <Avatar quale={avatar} dimensione={120} anello classe="ld-palco-mascotte" />
              <span className="ld-avatar-matita" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4" />
                </svg>
              </span>
            </button>
          }
        />
        {email && <p className="nota-centrata">{t("account.registrati_email", { email })}</p>}
        <form
          className="account-modulo"
          onSubmit={(e) => {
            e.preventDefault();
            if (!nome.trim() || !data) return;
            setInvio(true);
            setErrore(null);
            registra(nome.trim(), data)
              .catch((x: unknown) => setErrore(codiceErrore(x)))
              .finally(() => setInvio(false));
          }}
        >
          <label className="campo-etichettato">
            <span className="etichetta">{t("account.data_nascita")}</span>
            <input className="campo" type="date" required max={oggi} min="1900-01-01" value={data} onChange={(e) => setData(e.target.value)} />
            <span className="nota">{t("account.data_nascita_nota", { eta: minima })}</span>
          </label>
          <CampoNome valore={nome} onCambia={setNome} sempre />
          {errore && (
            <p className="admin-errore" role="alert">
              {t(`account.errore_${errore}`, { defaultValue: t("account.errore_generico"), eta: minima })}
            </p>
          )}
          <div className="azioni-riga">
            <button type="button" className="pulsante pulsante-secondario" onClick={annullaRegistrazione}>
              {t("generale.annulla")}
            </button>
            <button type="submit" className="pulsante pulsante-primario" disabled={invio || !nome.trim() || !data}>
              {t("account.registrati")}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
